import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  InputBase,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ThumbUp as HelpfulIcon,
  Folder as FolderIcon,
  Article as ArticleIcon,
  Close as CloseIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Publish as PublishIcon,
  Drafts as DraftIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import api, { extractData } from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

interface Category {
  id: string;
  name: string;
  description: string;
  icon: string;
  sort_order: number;
  article_count: string;
}

interface Article {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category_id: string;
  category_name: string;
  status: string;
  tags: string[];
  author_name: string;
  view_count: number;
  helpful_count: number;
  created_at: string;
  updated_at: string;
}

const HelpdeskKnowledgeBase: React.FC = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [categories, setCategories] = useState<Category[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [totalArticles, setTotalArticles] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Article detail / editor
  const [viewArticle, setViewArticle] = useState<Article | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<string | null>(null);
  const [articleForm, setArticleForm] = useState({ title: '', content: '', categoryId: '', status: 'draft' as string, tags: '' });
  const [saving, setSaving] = useState(false);

  // Category dialog
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<string | null>(null);
  const [catForm, setCatForm] = useState({ name: '', description: '', icon: 'folder' });

  const fetchCategories = async () => {
    try {
      const res = await api.get('/support/kb/categories');
      setCategories(extractData(res) || []);
    } catch { /* ignore */ }
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedCategory) params.set('categoryId', selectedCategory);
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/support/kb/articles?${params.toString()}`);
      const data = extractData(res) as { articles?: any[]; total?: number };
      setArticles(data.articles || []);
      setTotalArticles(data.total || 0);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchArticles(); }, [search, selectedCategory, statusFilter]);

  const handleSaveArticle = async () => {
    try {
      setSaving(true);
      const payload = {
        title: articleForm.title,
        content: articleForm.content,
        categoryId: articleForm.categoryId || undefined,
        status: articleForm.status,
        tags: articleForm.tags ? articleForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      if (editingArticle) {
        await api.put(`/support/kb/articles/${editingArticle}`, payload);
      } else {
        await api.post('/support/kb/articles', payload);
      }
      setEditorOpen(false);
      fetchArticles();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (id: string) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await api.delete(`/support/kb/articles/${id}`);
      setViewArticle(null);
      fetchArticles();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to delete'); }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCat) {
        await api.put(`/support/kb/categories/${editingCat}`, catForm);
      } else {
        await api.post('/support/kb/categories', catForm);
      }
      setCatDialogOpen(false);
      fetchCategories();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to save category'); }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm('Delete this category? Articles will be uncategorized.')) return;
    try {
      await api.delete(`/support/kb/categories/${id}`);
      if (selectedCategory === id) setSelectedCategory('');
      fetchCategories();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to delete'); }
  };

  const handleMarkHelpful = async (id: string) => {
    try {
      await api.post(`/support/kb/articles/${id}/helpful`);
      if (viewArticle?.id === id) {
        setViewArticle({ ...viewArticle, helpful_count: viewArticle.helpful_count + 1 });
      }
    } catch { /* ignore */ }
  };

  const openCreateArticle = () => {
    setEditingArticle(null);
    setArticleForm({ title: '', content: '', categoryId: selectedCategory, status: 'draft', tags: '' });
    setEditorOpen(true);
  };

  const openEditArticle = (a: Article) => {
    setEditingArticle(a.id);
    setArticleForm({
      title: a.title,
      content: a.content,
      categoryId: a.category_id || '',
      status: a.status,
      tags: (a.tags || []).join(', '),
    });
    setEditorOpen(true);
  };

  // Article Detail View
  if (viewArticle) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton size="small" onClick={() => setViewArticle(null)}><BackIcon fontSize="small" /></IconButton>
          <Typography sx={{ fontWeight: 600, flex: 1 }} noWrap>{viewArticle.title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={viewArticle.status}
              size="small"
              sx={{
                height: 22, fontSize: '0.72rem', fontWeight: 600, textTransform: 'capitalize',
                bgcolor: viewArticle.status === 'published' ? '#dcfce7' : '#fef3c7',
                color: viewArticle.status === 'published' ? '#16a34a' : '#d97706',
              }}
            />
            <Tooltip title="Was this helpful?">
              <IconButton size="small" onClick={() => handleMarkHelpful(viewArticle.id)}>
                <HelpfulIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {isAdmin && (
              <>
                <Tooltip title="Edit"><IconButton size="small" onClick={() => { openEditArticle(viewArticle); setViewArticle(null); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteArticle(viewArticle.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 3, maxWidth: 900, mx: 'auto', width: '100%' }}>
          <Paper sx={{ p: 4, borderRadius: '12px', border: '1px solid #e5e7eb' }} elevation={0}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{viewArticle.title}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, color: '#6b7280', fontSize: '0.85rem' }}>
              <Typography sx={{ fontSize: '0.85rem' }}>By {viewArticle.author_name}</Typography>
              <Typography sx={{ fontSize: '0.85rem' }}>· {formatDistanceToNow(parseISO(viewArticle.updated_at), { addSuffix: true })}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><ViewIcon sx={{ fontSize: 16 }} />{viewArticle.view_count} views</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><HelpfulIcon sx={{ fontSize: 16 }} />{viewArticle.helpful_count} helpful</Box>
            </Box>
            {viewArticle.category_name && (
              <Chip label={viewArticle.category_name} size="small" icon={<FolderIcon />} sx={{ mb: 2 }} />
            )}
            {viewArticle.tags?.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                {viewArticle.tags.map((tag, i) => (
                  <Chip key={i} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.72rem', height: 22 }} />
                ))}
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Box sx={{
              '& p': { marginBottom: '1em', lineHeight: 1.7 },
              '& h1,& h2,& h3': { marginTop: '1.5em', marginBottom: '0.5em' },
              '& ul,& ol': { paddingLeft: '1.5em', marginBottom: '1em' },
              '& li': { marginBottom: '0.25em' },
              '& pre': { bgcolor: '#f3f4f6', p: 2, borderRadius: '8px', overflow: 'auto', fontSize: '0.85rem' },
              '& code': { bgcolor: '#f3f4f6', px: 0.5, borderRadius: '4px', fontSize: '0.85rem' },
              fontSize: '0.95rem', color: '#1e293b', lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
            }}>
              {viewArticle.content}
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Knowledge Base</Typography>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f3f4f6', borderRadius: '8px', px: 1.5, py: 0.25, maxWidth: 280 }}>
          <SearchIcon sx={{ color: '#9ca3af', fontSize: 18, mr: 0.5 }} />
          <InputBase
            placeholder="Search articles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, fontSize: '0.85rem', '& input': { py: 0.5 } }}
          />
        </Box>
        {isAdmin && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} displayEmpty sx={{ fontSize: '0.85rem', borderRadius: '8px' }}>
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => { fetchCategories(); fetchArticles(); }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {isAdmin && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreateArticle}
            sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.85rem' }}>
            New Article
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mx: 3, mt: 1 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Categories Sidebar */}
        <Box sx={{ width: 240, bgcolor: '#fff', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#4b5563' }}>Categories</Typography>
            {isAdmin && (
              <Tooltip title="Add Category">
                <IconButton size="small" onClick={() => { setEditingCat(null); setCatForm({ name: '', description: '', icon: 'folder' }); setCatDialogOpen(true); }}>
                  <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
            <ListItemButton
              selected={selectedCategory === ''}
              onClick={() => setSelectedCategory('')}
              sx={{ py: 1, px: 2 }}
            >
              <ListItemText primary={<Typography sx={{ fontSize: '0.85rem', fontWeight: selectedCategory === '' ? 600 : 400 }}>All Articles</Typography>} />
              <Chip label={totalArticles} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
            </ListItemButton>
            {categories.map((cat) => (
              <ListItemButton
                key={cat.id}
                selected={selectedCategory === cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                sx={{ py: 1, px: 2 }}
              >
                <FolderIcon sx={{ fontSize: 18, color: '#6b7280', mr: 1.5 }} />
                <ListItemText
                  primary={<Typography sx={{ fontSize: '0.85rem', fontWeight: selectedCategory === cat.id ? 600 : 400 }} noWrap>{cat.name}</Typography>}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip label={cat.article_count || '0'} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                  {isAdmin && (
                    <>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingCat(cat.id); setCatForm({ name: cat.name, description: cat.description || '', icon: cat.icon || 'folder' }); setCatDialogOpen(true); }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }}>
                        <DeleteIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                      </IconButton>
                    </>
                  )}
                </Box>
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Articles List */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : articles.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <ArticleIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
              <Typography color="text.secondary">No articles found</Typography>
              {isAdmin && (
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openCreateArticle} sx={{ mt: 2, textTransform: 'none' }}>
                  Create First Article
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {articles.map((article) => (
                <Paper
                  key={article.id}
                  elevation={0}
                  sx={{
                    p: 2.5, borderRadius: '10px', border: '1px solid #e5e7eb', cursor: 'pointer',
                    '&:hover': { borderColor: '#93c5fd', bgcolor: '#fafbff' },
                  }}
                  onClick={() => setViewArticle(article)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <ArticleIcon sx={{ fontSize: 22, color: '#6366f1', mt: 0.3, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }} noWrap>{article.title}</Typography>
                        <Chip
                          label={article.status}
                          size="small"
                          icon={article.status === 'published' ? <PublishIcon sx={{ fontSize: '14px !important' }} /> : <DraftIcon sx={{ fontSize: '14px !important' }} />}
                          sx={{
                            height: 22, fontSize: '0.7rem', fontWeight: 600,
                            bgcolor: article.status === 'published' ? '#dcfce7' : '#fef3c7',
                            color: article.status === 'published' ? '#16a34a' : '#d97706',
                          }}
                        />
                      </Box>
                      <Typography sx={{ color: '#6b7280', fontSize: '0.83rem', mb: 0.75, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {article.excerpt}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#9ca3af', fontSize: '0.75rem' }}>
                        {article.category_name && <Chip label={article.category_name} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />}
                        <span>{article.author_name}</span>
                        <span>·</span>
                        <span>{formatDistanceToNow(parseISO(article.updated_at), { addSuffix: true })}</span>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <ViewIcon sx={{ fontSize: 14 }} />{article.view_count}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <HelpfulIcon sx={{ fontSize: 14 }} />{article.helpful_count}
                        </Box>
                      </Box>
                    </Box>
                    {isAdmin && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditArticle(article); }}><EditIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteArticle(article.id); }}><DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} /></IconButton></Tooltip>
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Article Editor Dialog */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px', height: '80vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {editingArticle ? 'Edit Article' : 'New Article'}
          <IconButton size="small" onClick={() => setEditorOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Title" required fullWidth size="small" value={articleForm.title} onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Category</InputLabel>
              <Select label="Category" value={articleForm.categoryId} onChange={(e) => setArticleForm({ ...articleForm, categoryId: e.target.value })}>
                <MenuItem value="">None</MenuItem>
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={articleForm.status} onChange={(e) => setArticleForm({ ...articleForm, status: e.target.value })}>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Tags (comma-separated)" size="small" fullWidth value={articleForm.tags} onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })} />
          </Box>
          <TextField
            label="Content"
            required
            fullWidth
            multiline
            value={articleForm.content}
            onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
            sx={{ flex: 1, '& .MuiInputBase-root': { flex: 1, alignItems: 'flex-start' } }}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.9rem' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditorOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveArticle}
            disabled={saving || !articleForm.title || !articleForm.content}
            sx={{ textTransform: 'none', borderRadius: '8px' }}>
            {saving ? 'Saving...' : editingArticle ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={catDialogOpen} onClose={() => setCatDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle>{editingCat ? 'Edit Category' : 'New Category'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Name" required fullWidth size="small" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} />
            <TextField label="Description" fullWidth multiline rows={2} size="small" value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCatDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCategory} disabled={!catForm.name} sx={{ textTransform: 'none', borderRadius: '8px' }}>
            {editingCat ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HelpdeskKnowledgeBase;
